"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_FEATURE_FLAGS,
  fetchFeatureFlags,
  getFeatureFlagMessage,
  isFeatureEnabled,
  softBlockState,
  type FeatureFlagsResponse,
  type ProductFeatureFlag,
} from "@project-x/api-clients";

/** Poll interval matching gateway Cache-Control public, max-age=30. */
export const FEATURE_FLAGS_POLL_MS = 30_000;

const DEFAULT_ENDPOINT = "/api/feature-flags";

export interface UseFeatureFlagsResult {
  /** Latest flags snapshot (fail-open defaults until first successful fetch). */
  flags: FeatureFlagsResponse;
  /** True after the first fetch attempt completes (success or failure). */
  loaded: boolean;
  /** Re-fetch immediately. */
  refresh: () => Promise<void>;
  /** Whether a product entrance is currently allowed. */
  isEnabled: (flag: ProductFeatureFlag) => boolean;
  /** Soft-block copy for a disabled flag (maintenance-aware). */
  messageFor: (flag: ProductFeatureFlag) => string;
  /** Convenience: { blocked, message } for a flag. */
  softBlock: (flag: ProductFeatureFlag) => { blocked: boolean; message: string | null };
}

/**
 * useFeatureFlags polls the public feature-flags BFF (~30s) and refetches on
 * window focus / visibility. Failures keep the previous snapshot (fail-open
 * defaults on first load) so a flags outage never hard-blocks the UI.
 */
export function useFeatureFlags(endpoint: string = DEFAULT_ENDPOINT): UseFeatureFlagsResult {
  const [flags, setFlags] = useState<FeatureFlagsResponse>(DEFAULT_FEATURE_FLAGS);
  const [loaded, setLoaded] = useState(false);
  const endpointRef = useRef(endpoint);
  endpointRef.current = endpoint;

  const refresh = useCallback(async () => {
    try {
      const next = await fetchFeatureFlags(endpointRef.current);
      setFlags(next);
    } catch (err) {
      if (process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_LOG_LEVEL === "debug") {
        console.debug("[useFeatureFlags] fetch failed (fail-open)", err);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const intervalId = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      void refresh();
    }, FEATURE_FLAGS_POLL_MS);

    const onFocus = () => {
      void refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  return {
    flags,
    loaded,
    refresh,
    isEnabled: (flag) => isFeatureEnabled(flags, flag),
    messageFor: (flag) => getFeatureFlagMessage(flags, flag),
    softBlock: (flag) => softBlockState(flags, flag),
  };
}
